import { spacing, breakpoints } from '@naf/theme';
import { nafColor } from '@nafcore/theme';
import styled from '@emotion/styled';
import { AccordionBlockWrapper } from '@components/SectionBlocks/blocks/AccordionBlock';
import { Container as IframeBlockContainer } from '@components/SectionBlocks/blocks/IframeBlock';
import { LivestreamBlockWrapper } from '@components/SectionBlocks/blocks/LivestreamBlock';
import { HeadContainer as NewsfeedHeaderContainer } from '@components/SectionBlocks/blocks/NewsFeedBlock/NewsFeedBlock.styles';
import { HeaderContainer as QnaHeaderContainer } from '@components/SectionBlocks/blocks/QnaFeedBlock/Header';
import { TextBlockContainer } from '@components/SectionBlocks/blocks/TextBlock';
import { ChartWrapper } from '@components/SectionBlocks/Testshowcase/Results/Styles';
import { ShowcaseBlockWrapper } from '@components/SectionBlocks/Testshowcase/ShowcaseBlock';
import { BlockHeader as ColumnBlockHeader } from '@components/SectionBlocks/blocks/MultiRowArticleBlock';
import { ColumnWrapper, IngressText as ColumnBlockIngressText } from '@components/SectionBlocks/ColumnBlock.styles';

export const LeftColumn = styled.div<{ $isValgPage?: boolean; $isFormSubmitted?: boolean }>`
  display: flex;
  flex-flow: column nowrap;
  flex: 2 0 calc(66% - 2 / 3 * ${spacing.space48});
  box-sizing: content-box;
  width: 90%;

  @media (width <= calc(${breakpoints.xl} + 9px)) {
    flex-basis: 100%;
  }

  @media (width > ${breakpoints.m}) {
    ${ShowcaseBlockWrapper},
    ${LivestreamBlockWrapper}, ${NewsfeedHeaderContainer}, ${QnaHeaderContainer}, ${IframeBlockContainer} {
      h1,
      h1 + p {
        max-width: 50ch;
      }
    }

    ${AccordionBlockWrapper} {
      h2,
      h2 + p {
        max-width: 50ch;
      }
    }

    ${ColumnBlockHeader} h2,
    ${ColumnBlockIngressText} {
      max-width: 60ch;
    }

    ${TextBlockContainer} {
      max-width: 75ch;
    }

    ${ChartWrapper} {
      h2,
      h2 + p {
        max-width: 50ch;
      }
    }
  }

  ${ColumnWrapper} {
    @media (max-width: ${breakpoints.m}) {
      width: calc(100vw - 48px);
      margin: -${spacing.space4} -${spacing.space24};
      padding: ${spacing.space4} ${spacing.space24};
    }
  }

  ${({ $isValgPage, $isFormSubmitted }) =>
    $isValgPage &&
    `
    ${$isFormSubmitted ? '.TextAndImageBlock h2:has(~ .DynamicForm) { display: none; }' : ''}

    .FieldWrapper {
    margin-bottom: 32px;
    

    @media (min-width: ${breakpoints.xl}) {
    max-width: 70%;
    }
    }

    .GroupFieldWrapper {
    margin-top: -32px;
    margin-bottom: 16px;
    
    @media (min-width: ${breakpoints.xl}) {
    max-width: 70%;
    }
    
    }

    @media (min-width: ${breakpoints.xl}) {
      max-width: 90%;
    }

    .TextAndImageBlock {

    /* Simba sets flex-shrink: 0 on both items, so basis + gap overflows the container; with row-reverse it spills left */
    > * {
      flex-shrink: 1;
    }

    p {
      margin: 0;
    }

    h2 {
    margin: 0;
    }

    label {
    width: 90%;
    }

    }
  `}
`;

export const RightColumn = styled.div`
  display: flex;
  flex-flow: column nowrap;
  flex: 1 0 calc(33% - 1 / 3 * ${spacing.space48});

  @media (width <= calc(${breakpoints.xl} + 9px)) {
    display: none;
  }
`;

export const ChatBubbleWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: ${spacing.space120} ${spacing.space24} ${spacing.space64} ${spacing.space24};

  @media (max-width: ${breakpoints.m}) {
    box-sizing: border-box;
    align-items: center;
    aspect-ratio: 4 / 3;
    padding: ${spacing.space24};
  }
`;

export const ChatBubble = styled.div`
  position: relative;
  box-sizing: border-box;
  width: 100%;
  max-width: 340px;
  min-height: 130px;
  padding: ${spacing.space32};
  background-color: ${nafColor.signature.yellow};
  border-radius: 6px;
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (max-width: ${breakpoints.m}) {
    min-height: 100px;
    margin-top: ${spacing.space56};
  }

  /* Tail */
  &::after {
    content: '';
    position: absolute;
    bottom: -${spacing.space24};
    left: ${spacing.space32};
    border-style: solid;
    border-width: ${spacing.space24} ${spacing.space40} 0 0;
    border-color: ${nafColor.signature.yellow} transparent transparent transparent;
  }
`;

export const ChatBubbleSubtitle = styled.p`
  margin: 0;
  font-size: 1.125rem;
  font-style: italic;
  color: ${nafColor.signature.black};
`;

export const ChatBubbleTitle = styled.h2`
  margin: 0;
  font-size: clamp(1.5rem, 6vw, 2rem);
  font-weight: 700;
  line-height: 1.15;
  color: ${nafColor.signature.black};
`;

export const ColumnContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  flex-grow: 1;
  box-sizing: border-box;
  column-gap: ${spacing.space48};
  margin: 0 auto;
  min-height: 50vh;
  background-color: ${nafColor.signature.white};
  border-radius: ${spacing.space4};
  padding: ${spacing.space24} ${spacing.space40};

  @media (width > ${breakpoints.m}) {
    width: 90%;
    max-width: 1600px;
  }

  @media (max-width: ${breakpoints.m}) {
    width: 100%;
    padding: ${spacing.space24};
  }
`;

type BackgroundSources = { x1: string; x2: string };

const backgroundImage = ({ x1, x2 }: BackgroundSources) => `
  background-image: url('${x1}');
  background-image: image-set(url('${x1}') 1x, url('${x2}') 2x);
`;

export const PageContent = styled.div<{ $background?: BackgroundSources; $halfHeightBackground?: boolean }>`
  min-height: 100vh;
  ${({ $background }) => ($background ? backgroundImage($background) : '')}
  background-repeat: no-repeat;
  background-position: center top;
  background-size: 100% auto;

  @media (height >= 69vw) {
    background-size: auto 80vh;
  }

  ${({ $background, $halfHeightBackground }) =>
    $background && $halfHeightBackground
      ? `
    @media (width > ${breakpoints.m}) {
      position: relative;
      isolation: isolate;
      background-image: none;

      &::before {
        content: '';
        position: absolute;
        inset: 0 0 auto 0;
        height: 70vh;
        z-index: -1;
        ${backgroundImage($background)}
        background-repeat: no-repeat;
        background-position: center top;
        background-size: cover;
      }
    }

    @media (max-width: ${breakpoints.m}) {
      position: relative;
      isolation: isolate;
      background-image: none;

      /* Keep aspect ratio in sync with ChatBubbleWrapper on mobile */
      &::before {
        content: '';
        position: absolute;
        inset: 0 0 auto 0;
        aspect-ratio: 4 / 3;
        z-index: -1;
        ${backgroundImage($background)}
        background-repeat: no-repeat;
        background-position: center top;
        background-size: cover;
      }
    }
  `
      : ''}
`;

export const FeedbackContainer = styled.div`
  margin: 0 auto;
  box-sizing: border-box;
  padding: 0 ${spacing.space40} ${spacing.space64} ${spacing.space40};

  @media (width > ${breakpoints.m}) {
    width: 90%;
    max-width: 1600px;
  }

  @media (width <= ${breakpoints.m}) {
    width: 100%;
    padding: 0 ${spacing.space24} ${spacing.space64} ${spacing.space24};
  }
`;
